import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import Nav from "@/components/Nav";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarRange, Play, Loader2, Plus, X, Sparkles, TrendingUp } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export default function PeriodAnalysis() {
  const { activeId: propId, lang } = useApp();
  const L = (it, en) => (lang === "en" ? en : it);
  const [comps, setComps] = useState([]);
  const [start, setStart] = useState(todayIso());
  const [end, setEnd] = useState(addDays(60));
  const [calib, setCalib] = useState({});
  const [extraInput, setExtraInput] = useState("");
  const [extraDates, setExtraDates] = useState([]);
  const [running, setRunning] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [pollId, setPollId] = useState(null);

  const loadComps = useCallback(async () => {
    if (!propId) return;
    try {
      const { data } = await api.get(`/properties/${propId}/market-positioning?lang=${lang}`);
      setComps((data?.selected || []).slice(0, 5));
    } catch { setComps([]); }
  }, [propId, lang]);

  const loadLatest = useCallback(async () => {
    if (!propId) return;
    try {
      const { data } = await api.get(`/properties/${propId}/period-analysis`);
      if (data?.has_data) {
        setAnalysis(data.analysis);
        if (data.analysis.status === "running") setPollId(data.analysis.id);
      }
    } catch { /* none yet */ }
  }, [propId]);

  useEffect(() => { loadComps(); loadLatest(); }, [loadComps, loadLatest]);

  // poll while running
  useEffect(() => {
    if (!pollId || !propId) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/properties/${propId}/period-analysis?id=${pollId}`);
        if (data?.has_data && data.analysis.status !== "running") {
          setAnalysis(data.analysis); setPollId(null); setRunning(false);
          if (data.analysis.status === "done") toast.success(L("Analisi periodo pronta", "Period analysis ready"));
          else toast.error(L("Errore nella scansione periodo", "Period scan error"));
        }
      } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [pollId, propId]); // eslint-disable-line

  const addExtra = () => {
    const d = extraInput;
    if (d && !extraDates.includes(d)) setExtraDates([...extraDates, d].sort());
    setExtraInput("");
  };

  const run = async () => {
    if (!comps.length) { toast.error(L("Nessun competitor selezionato", "No selected competitors")); return; }
    // Stima chiamate Apify PRIMA di lanciare, così decidi tu.
    try {
      const { data: est } = await api.post(`/properties/${propId}/period-analysis/estimate`, { start, end, extra_dates: extraDates });
      const ok = window.confirm(L(
        `Questa analisi userà circa ${est.estimated_apify_calls} chiamate Apify (${est.dates} date × ${est.competitors} competitor + il prezzo della tua struttura).\n\nVuoi procedere?`,
        `This analysis will use about ${est.estimated_apify_calls} Apify calls (${est.dates} dates × ${est.competitors} competitors + your own price).\n\nProceed?`
      ));
      if (!ok) return;
    } catch (e) {
      // se la stima fallisce, chiedo comunque conferma generica
      if (!window.confirm(L("Impossibile stimare le chiamate Apify. Vuoi procedere comunque?", "Could not estimate Apify calls. Proceed anyway?"))) return;
    }
    setRunning(true);
    try {
      const { data } = await api.post(`/properties/${propId}/period-analysis/scan`, {
        start, end, extra_dates: extraDates, calibration: calib, lang,
      });
      toast.success(L(`Scansione avviata su ${data.dates_planned} date`, `Scan started on ${data.dates_planned} dates`), {
        description: L("Uso di Apify in corso. I risultati compaiono tra qualche minuto.", "Using Apify. Results appear in a few minutes."),
      });
      setAnalysis({ status: "running", start, end });
      setPollId(data.analysis_id);
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || L("Errore avvio scansione", "Scan start error"));
    }
  };

  const isRunning = running || (analysis?.status === "running");

  return (
    <div className="App min-h-screen"><Nav />
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="period-analysis-page">
      <div className="flex items-center gap-2 mb-1"><CalendarRange className="w-7 h-7 text-primary" />
        <h1 className="text-3xl font-bold">{L("Analizza periodo specifico", "Analyze specific period")}</h1></div>
      <p className="text-muted-foreground mb-6 max-w-3xl">{L(
        "Scegli un periodo (e/o date specifiche) e lancia la scansione dei tuoi 5 competitor selezionati con la stessa logica dei 6 mesi (2 feriali + ven/sab/dom ogni 2 settimane + festività). I prezzi sono NETTI (tassa di soggiorno e colazione come da config) con lo sconto che imposti QUI, valido solo per questo periodo. I risultati salvano gli snapshot e riempiono anche la griglia di ogni competitor.",
        "Pick a period (and/or specific dates) and scan your 5 selected competitors with the same logic as the 6-month grid. Prices are NET with the discount you set HERE, valid only for this period. Results also fill each competitor's grid.")}</p>

      {/* Controls */}
      <div className="bg-card border border-border rounded-xl p-5 mb-6" data-testid="period-controls">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div><label className="text-xs font-bold uppercase text-muted-foreground">{L("Dal", "From")}</label>
            <input data-testid="period-start" type="date" value={start} onChange={(e) => setStart(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background" /></div>
          <div><label className="text-xs font-bold uppercase text-muted-foreground">{L("Al", "To")}</label>
            <input data-testid="period-end" type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)}
              className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background" /></div>
          <div className="lg:col-span-2"><label className="text-xs font-bold uppercase text-muted-foreground">{L("Date specifiche extra (opzionale)", "Extra specific dates (optional)")}</label>
            <div className="flex gap-2 mt-1">
              <input data-testid="period-extra-input" type="date" value={extraInput} onChange={(e) => setExtraInput(e.target.value)}
                className="flex-1 px-3 py-2 border border-border rounded-md bg-background" />
              <button data-testid="period-extra-add" onClick={addExtra} className="px-3 py-2 rounded-md bg-secondary hover:bg-accent inline-flex items-center gap-1 text-sm font-semibold"><Plus className="w-4 h-4" />{L("Aggiungi", "Add")}</button>
            </div>
            {extraDates.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {extraDates.map((d) => (
                  <span key={d} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary inline-flex items-center gap-1">{d}
                    <button onClick={() => setExtraDates(extraDates.filter((x) => x !== d))}><X className="w-3 h-3" /></button></span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="text-xs font-bold uppercase text-muted-foreground mb-2">{L("Competitor selezionati + sconto SOLO per questo periodo", "Selected competitors + discount ONLY for this period")}</div>
        <div className="grid sm:grid-cols-2 gap-2 mb-4">
          {comps.length === 0 && <p className="text-sm text-muted-foreground">{L("Nessun competitor selezionato. Selezionali nella pagina Competitor.", "No selected competitors. Pick them on the Competitor page.")}</p>}
          {comps.map((c) => (
            <div key={c.competitor_id} data-testid={`period-comp-${c.competitor_id}`} className="flex items-center justify-between gap-3 border border-border rounded-lg px-3 py-2">
              <span className="text-sm font-medium truncate">{c.name}</span>
              <div className="flex items-center gap-1 shrink-0">
                <input data-testid={`period-calib-${c.competitor_id}`} type="number" step="1" placeholder="0"
                  value={calib[c.competitor_id] ?? ""} onChange={(e) => setCalib({ ...calib, [c.competitor_id]: e.target.value === "" ? "" : Number(e.target.value) })}
                  className="w-20 px-2 py-1.5 border border-border rounded-md bg-background text-right font-data" />
                <span className="text-sm text-muted-foreground">% {L("sconto", "disc.")}</span>
              </div>
            </div>
          ))}
        </div>

        <button data-testid="period-run-btn" disabled={isRunning || !comps.length} onClick={run}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary text-primary-foreground font-bold hover:opacity-90 disabled:opacity-50 transition-opacity">
          {isRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {isRunning ? L("Scansione in corso…", "Scanning…") : L("Esegui scansione", "Run scan")}
        </button>
        <p className="text-[11px] text-muted-foreground mt-2">{L("La scansione usa Apify e consuma credito.", "The scan uses Apify and consumes credit.")}</p>
      </div>

      {/* Results */}
      {analysis?.status === "done" && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="period-results">
          <div className="text-sm text-muted-foreground mb-4">{L("Periodo", "Period")}: <b>{analysis.start} → {analysis.end}</b></div>

          {/* Combined positioning chart: competitor average vs your property */}
          {(analysis.summary_series || []).some((s) => s.comp_avg != null || s.own != null) && (
            <div className="bg-card border border-border rounded-xl p-5 mb-6" data-testid="period-summary-chart">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                <div className="font-semibold">{L("Posizionamento nel periodo — Tu vs media competitor", "Positioning in the period — You vs competitor average")}</div>
                {analysis.own?.avg_net != null && (
                  <div className="text-xs text-muted-foreground" data-testid="period-own-summary">
                    {L("Tuo netto medio", "Your avg net")}: <b className="text-foreground">€{analysis.own.avg_net}</b>
                    {" · "}{L("min", "min")} €{analysis.own.min_net ?? "—"} · max €{analysis.own.max_net ?? "—"} · {analysis.own.points} {L("date", "dates")}
                  </div>
                )}
              </div>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <LineChart data={analysis.summary_series} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 10 }} width={40} />
                    <Tooltip formatter={(v, n) => [`€${v}`, n === "own" ? L("tuo netto", "your net") : L("media competitor", "competitor avg")]} labelStyle={{ fontSize: 11 }} />
                    <Legend formatter={(n) => (n === "own" ? L("La tua struttura", "Your property") : L("Media competitor", "Competitor average"))} wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="comp_avg" name="comp_avg" stroke="var(--primary)" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                    <Line type="monotone" dataKey="own" name="own" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 2 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {analysis.own?.avg_net == null && (
                <p className="text-xs text-muted-foreground mt-2">{L("Nessun prezzo della tua struttura rilevato per questo periodo (verifica l'URL Booking o i prezzi manuali).", "No price detected for your property in this period (check your Booking URL or manual prices).")}</p>
              )}
            </div>
          )}

          {analysis.report && (
            <div className="bg-card border border-border rounded-xl p-5 mb-6" data-testid="period-ai-report">
              <div className="flex items-center gap-2 font-semibold mb-2 text-primary"><Sparkles className="w-5 h-5" />{analysis.report.headline || L("Relazione AI", "AI report")}</div>
              <p className="text-sm mb-3">{analysis.report.assessment}</p>
              {(analysis.report.advice || []).length > 0 && (
                <ul className="space-y-1.5">
                  {analysis.report.advice.map((a, i) => (
                    <li key={i} className="text-sm flex items-start gap-2"><TrendingUp className="w-4 h-4 mt-0.5 text-primary shrink-0" />{a}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
            <span className="inline-flex items-center gap-1"><span className="inline-block w-4 h-0.5 rounded" style={{ background: "var(--primary)" }} />{L("competitor (netto)", "competitor (net)")}</span>
            <span className="inline-flex items-center gap-1"><span className="inline-block w-4 h-0.5 rounded" style={{ background: "#f59e0b" }} />{L("la tua struttura (netto)", "your property (net)")}</span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {(analysis.results || []).map((c) => (
              <div key={c.competitor_id} data-testid={`period-result-${c.competitor_id}`} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold truncate">{c.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-secondary">{c.breakfast_label === "included" ? L("colazione inclusa", "breakfast incl.") : c.breakfast_label === "excluded" ? L("senza colazione", "no breakfast") : L("colazione n.d.", "breakfast n/a")}</span>
                </div>
                <div className="text-xs text-muted-foreground mb-2">
                  {L("Netto medio", "Avg net")}: <b className="text-foreground">{c.avg_net != null ? `€${c.avg_net}` : "—"}</b> · {L("min", "min")} €{c.min_net ?? "—"} · max €{c.max_net ?? "—"} · {c.points} {L("date", "dates")} · {L("sconto", "disc.")} {c.calibration_pct}%
                </div>
                {c.series?.length > 0 ? (
                  <div style={{ width: "100%", height: 160 }}>
                    <ResponsiveContainer>
                      <LineChart data={c.series.filter((s) => s.net != null || s.own != null)} margin={{ top: 6, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                        <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10 }} width={36} />
                        <Tooltip formatter={(v, n) => [`€${v}`, n === "own" ? L("tuo netto", "your net") : L("netto", "net")]} labelStyle={{ fontSize: 11 }} />
                        <Line type="monotone" dataKey="net" name="net" stroke="var(--primary)" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                        <Line type="monotone" dataKey="own" name="own" stroke="#f59e0b" strokeWidth={2} strokeDasharray="4 3" dot={false} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : <p className="text-xs text-muted-foreground py-6 text-center">{L("Nessun prezzo rilevato in questo periodo.", "No prices found for this period.")}</p>}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {analysis?.status === "error" && (
        <div className="bg-card border border-border rounded-xl p-5 text-sm text-red-500" data-testid="period-error">
          {L("La scansione non è riuscita: ", "The scan failed: ")}{analysis.error || L("errore sconosciuto", "unknown error")}
        </div>
      )}
    </div>
    </div>
  );
}
